/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import type { EditorProps } from "@monaco-editor/react";
import type { PromptCatalog, MacroDefinition, MacroTemplateDefinition, PartialDefinition } from "./promptCatalog";

type ResolvedMacroDefinition = MacroDefinition & {
  template?: string;
};

const installedLanguageIds = new Set<string>();
const catalogRegistry = new Map<string, PromptCatalog>();

const emptyCatalog: PromptCatalog = {
  partials: [],
  macroTemplates: [],
};

const triggerSuggestCommand = {
  id: "editor.action.triggerSuggest",
  title: "Trigger suggest",
};

type MonacoInstance = Parameters<NonNullable<EditorProps["beforeMount"]>>[0];

type EditorPosition = {
  lineNumber: number;
  column: number;
};

type EditorRange = {
  startLineNumber: number;
  endLineNumber: number;
  startColumn: number;
  endColumn: number;
};

export function installJinjaPromptLanguage(
  monaco: MonacoInstance,
  catalog: PromptCatalog,
  languageId = "jinja-prompt"
): void {
  catalogRegistry.set(languageId, normalizeCatalog(catalog));

  if (installedLanguageIds.has(languageId)) {
    return;
  }

  installedLanguageIds.add(languageId);

  monaco.languages.register({
    id: languageId,
    extensions: [".jinja", ".j2", ".md.jinja", ".md.j2"],
    aliases: ["Jinja Prompt", "jinja-prompt"],
    mimetypes: ["text/x-jinja-prompt"],
  });

  monaco.languages.setMonarchTokensProvider(languageId, {
    tokenizer: {
      root: [
        [/\{#[-+]?/, { token: "comment", next: "@comment" }],
        [/\{\{[-+]?/, { token: "delimiter", next: "@expression" }],
        [/\{%[-+]?/, { token: "delimiter", next: "@statement" }],
      ],

      comment: [
        [/[-+]?#\}/, { token: "comment", next: "@pop" }],
        [/./, "comment"],
      ],

      expression: [
        [/\s+/, "white"],
        [/[-+]?\}\}/, { token: "delimiter", next: "@pop" }],
        [/'([^'\\]|\\.)*'/, "string"],
        [/"([^"\\]|\\.)*"/, "string"],
        [/\|[a-zA-Z_][\w]*/, "type.identifier"],
        [/\b(true|false|none)\b/, "keyword"],
        [/\b(and|or|not|in|is)\b/, "keyword"],
        [/[a-zA-Z_][\w]*/, "variable.name"],
        [/\d+(?:\.\d+)?/, "number"],
        [/[()[\],.:=]/, "delimiter"],
      ],

      statement: [
        [/\s+/, "white"],
        [/[-+]?%\}/, { token: "delimiter", next: "@pop" }],
        [/'([^'\\]|\\.)*'/, "string"],
        [/"([^"\\]|\\.)*"/, "string"],
        [
          /\b(include|import|from|as|with|without|context|ignore|missing|macro|endmacro|call|endcall|if|elif|else|endif|for|endfor|set|endset|filter|endfilter|block|endblock|extends|in|is|not|and|or|recursive)\b/,
          "keyword",
        ],
        [/[a-zA-Z_][\w]*/, "identifier"],
        [/\d+(?:\.\d+)?/, "number"],
        [/[()[\],.=]/, "delimiter"],
      ],
    },
  });

  monaco.languages.setLanguageConfiguration(languageId, {
    comments: {
      blockComment: ["{#", "#}"],
    },
    autoClosingPairs: [
      { open: "{#", close: "#}" },
      { open: "{{", close: "}}" },
      { open: "{%", close: "%}" },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
      { open: "(", close: ")" },
      { open: "[", close: "]" },
      { open: "{", close: "}" },
    ],
    surroundingPairs: [
      { open: '"', close: '"' },
      { open: "'", close: "'" },
      { open: "(", close: ")" },
      { open: "[", close: "]" },
      { open: "{", close: "}" },
    ],
  });

  monaco.languages.registerCompletionItemProvider(languageId, {
    triggerCharacters: [" ", "'", '"', ".", ",", "{", "%"],

    provideCompletionItems(
      model: { getValueInRange: (range: EditorRange) => string; getValue: () => string },
      position: EditorPosition
    ) {
      const currentCatalog = catalogRegistry.get(languageId) ?? emptyCatalog;
      const linePrefix = model.getValueInRange({
        startLineNumber: position.lineNumber,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });
      const fullText = model.getValue();

      const includeContext = getIncludeTemplateContext(linePrefix);
      if (includeContext) {
        return {
          suggestions: buildTemplateSuggestions(
            monaco,
            position,
            currentCatalog.partials,
            includeContext.quote,
            linePrefix
          ),
        };
      }

      const fromTemplateContext = getFromTemplateContext(linePrefix);
      if (fromTemplateContext) {
        return {
          suggestions: buildFromTemplateSuggestions(
            monaco,
            position,
            currentCatalog.macroTemplates,
            fromTemplateContext.quote,
            linePrefix
          ),
        };
      }

      const importTemplateContext = getImportTemplateContext(linePrefix);
      if (importTemplateContext) {
        return {
          suggestions: buildImportTemplateSuggestions(
            monaco,
            model,
            position,
            currentCatalog.macroTemplates,
            importTemplateContext.quote,
            linePrefix
          ),
        };
      }

      const fromImportContext = getFromImportContext(linePrefix);
      if (fromImportContext) {
        const macroTemplate = currentCatalog.macroTemplates.find(
          (entry) => entry.template === fromImportContext.template
        );

        if (!macroTemplate) {
          return { suggestions: [] };
        }

        return {
          suggestions: buildMacroImportSuggestions(monaco, position, macroTemplate),
        };
      }

      const aliasCallContext = getAliasMacroCallContext(linePrefix);
      if (aliasCallContext) {
        const importedTemplateAliases = collectImportedTemplateAliases(fullText);
        const templateName = importedTemplateAliases[aliasCallContext.alias];
        const macroTemplate = currentCatalog.macroTemplates.find((entry) => entry.template === templateName);

        if (!macroTemplate) {
          return { suggestions: [] };
        }

        return {
          suggestions: buildNamespacedMacroCallSuggestions(
            monaco,
            position,
            aliasCallContext.prefix,
            macroTemplate.macros.filter((macro) => !macro.name.startsWith("_"))
          ),
        };
      }

      const expressionMacroContext = getExpressionMacroContext(linePrefix);
      if (expressionMacroContext) {
        const localMacros = collectLocalMacros(fullText);
        const importedMacros = collectDirectImportedMacros(fullText, currentCatalog);
        const availableMacros = deduplicateMacros([...localMacros, ...importedMacros]);

        return {
          suggestions: buildDirectMacroSuggestions(monaco, position, expressionMacroContext.prefix, availableMacros),
        };
      }

      const statementKeywordContext = getStatementKeywordContext(linePrefix);
      if (statementKeywordContext) {
        return {
          suggestions: buildStatementKeywordSuggestions(monaco, position, linePrefix, statementKeywordContext.prefix),
        };
      }

      return { suggestions: [] };
    },
  });
}

function buildFromTemplateSuggestions(
  monaco: MonacoInstance,
  position: EditorPosition,
  items: MacroTemplateDefinition[],
  quote: "'" | '"' | null,
  linePrefix: string
) {
  const range = getTemplateInsertRange(linePrefix, position, quote);

  return items.map((item) => ({
    label: item.template,
    kind: monaco.languages.CompletionItemKind.File,
    insertText: quote ? item.template : `'${item.template}'`,
    documentation: item.description,
    range,
  }));
}

function getImportTemplateInsertRange(
  model: any,
  linePrefix: string,
  position: EditorPosition,
  quote: "'" | '"' | null
): EditorRange {
  if (!quote) {
    return {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: position.column,
      endColumn: position.column,
    };
  }

  const quoteIndex = linePrefix.lastIndexOf(quote);
  const lineContent = model.getLineContent(position.lineNumber);
  const nextChar = lineContent[position.column - 1] ?? "";
  const hasClosingQuote = nextChar === quote;

  return {
    startLineNumber: position.lineNumber,
    endLineNumber: position.lineNumber,
    startColumn: quoteIndex + 2,
    endColumn: hasClosingQuote ? position.column + 1 : position.column,
  };
}

function buildImportTemplateSuggestions(
  monaco: MonacoInstance,
  model: any,
  position: EditorPosition,
  items: MacroTemplateDefinition[],
  quote: "'" | '"' | null,
  linePrefix: string
) {
  const range = getImportTemplateInsertRange(model, linePrefix, position, quote);

  return items.map((item) => {
    const alias = deriveTemplateAlias(item.template);

    return {
      label: item.template,
      kind: monaco.languages.CompletionItemKind.File,
      insertText: quote ? `${item.template}${quote} as ${alias}` : `'${item.template}' as ${alias}`,
      documentation: item.description ? `${item.description}\n\nAlias: ${alias}` : `Alias: ${alias}`,
      range,
    };
  });
}

function normalizeCatalog(catalog: PromptCatalog): PromptCatalog {
  const partials = deduplicatePartials(catalog.partials).sort((left, right) => left.path.localeCompare(right.path));

  const macroTemplates = deduplicateMacroTemplates(catalog.macroTemplates)
    .map((template) => ({
      ...template,
      macros: deduplicateMacros(template.macros).sort((left, right) => left.name.localeCompare(right.name)),
    }))
    .sort((left, right) => left.template.localeCompare(right.template));

  return {
    partials,
    macroTemplates,
  };
}

function deduplicatePartials(partials: PartialDefinition[]): PartialDefinition[] {
  const map = new Map<string, PartialDefinition>();

  for (const partial of partials) {
    map.set(partial.path, partial);
  }

  return [...map.values()];
}

function deduplicateMacroTemplates(templates: MacroTemplateDefinition[]): MacroTemplateDefinition[] {
  const map = new Map<string, MacroTemplateDefinition>();

  for (const template of templates) {
    map.set(template.template, template);
  }

  return [...map.values()];
}

function deduplicateMacros<T extends MacroDefinition>(macros: T[]): T[] {
  const map = new Map<string, T>();

  for (const macro of macros) {
    map.set(macro.name, macro);
  }

  return [...map.values()];
}

function getIncludeTemplateContext(linePrefix: string): { quote: "'" | '"' | null } | null {
  const match = linePrefix.match(/\{%\s*include\s+(?:(['"])[^'"]*)?$/);
  if (!match) {
    return null;
  }

  return {
    quote: (match[1] as "'" | '"') ?? null,
  };
}

function getImportTemplateContext(linePrefix: string): { quote: "'" | '"' | null } | null {
  const match = linePrefix.match(/\{%\s*import\s+(?:(['"])[^'"]*)?$/);
  if (!match) {
    return null;
  }

  return {
    quote: (match[1] as "'" | '"') ?? null,
  };
}

function getFromTemplateContext(linePrefix: string): { quote: "'" | '"' | null } | null {
  const match = linePrefix.match(/\{%\s*from\s+(?:(['"])[^'"]*)?$/);
  if (!match) {
    return null;
  }

  return {
    quote: (match[1] as "'" | '"') ?? null,
  };
}

function getFromImportContext(linePrefix: string): { template: string } | null {
  const match = linePrefix.match(/\{%\s*from\s+['"]([^'"]+)['"]\s+import\s+([^%]*)$/);

  if (!match) {
    return null;
  }

  return {
    template: match[1],
  };
}

function getAliasMacroCallContext(linePrefix: string): { alias: string; prefix: string } | null {
  const match = linePrefix.match(/\{\{\s*([a-zA-Z_][\w]*)\.([a-zA-Z_][\w]*)?$/);

  if (!match) {
    return null;
  }

  return {
    alias: match[1],
    prefix: match[2] ?? "",
  };
}

function getExpressionMacroContext(linePrefix: string): { prefix: string } | null {
  const match = linePrefix.match(/\{\{\s*([a-zA-Z_][\w]*)?$/);

  if (!match) {
    return null;
  }

  return {
    prefix: match[1] ?? "",
  };
}

function getStatementKeywordContext(linePrefix: string): { prefix: string } | null {
  const match = linePrefix.match(/\{%\s*([a-zA-Z_]*)$/);

  if (!match) {
    return null;
  }

  return {
    prefix: match[1] ?? "",
  };
}

function buildTemplateSuggestions(
  monaco: MonacoInstance,
  position: EditorPosition,
  items: PartialDefinition[],
  quote: "'" | '"' | null,
  linePrefix: string
) {
  const range = getTemplateInsertRange(linePrefix, position, quote);

  return items.map((item) => ({
    label: item.path,
    kind: monaco.languages.CompletionItemKind.File,
    insertText: quote ? item.path : `'${item.path}'`,
    documentation: item.description,
    range,
  }));
}

function buildMacroImportSuggestions(
  monaco: MonacoInstance,
  position: EditorPosition,
  template: MacroTemplateDefinition
) {
  const { range } = getCurrentIdentifierRange(position, "");
  const macros = template.macros.filter((macro) => !macro.name.startsWith("_"));

  return macros.map((macro) => ({
    label: macro.name,
    kind: monaco.languages.CompletionItemKind.Function,
    insertText: macro.name,
    documentation: macro.description,
    range,
  }));
}

function buildNamespacedMacroCallSuggestions(
  monaco: MonacoInstance,
  position: EditorPosition,
  prefix: string,
  macros: MacroDefinition[]
) {
  const filteredMacros = prefix ? macros.filter((macro) => macro.name.startsWith(prefix)) : macros;

  const range = {
    startLineNumber: position.lineNumber,
    endLineNumber: position.lineNumber,
    startColumn: position.column - prefix.length,
    endColumn: position.column,
  };

  return filteredMacros.map((macro) => ({
    label: macro.name,
    kind: monaco.languages.CompletionItemKind.Function,
    insertText: buildMacroCallSnippet(macro),
    insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
    documentation: macro.description,
    range,
  }));
}

function buildDirectMacroSuggestions(
  monaco: MonacoInstance,
  position: EditorPosition,
  prefix: string,
  macros: ResolvedMacroDefinition[]
) {
  const filteredMacros = prefix ? macros.filter((macro) => macro.name.startsWith(prefix)) : macros;

  const range = {
    startLineNumber: position.lineNumber,
    endLineNumber: position.lineNumber,
    startColumn: position.column - prefix.length,
    endColumn: position.column,
  };

  return filteredMacros.map((macro) => ({
    label: macro.name,
    kind: monaco.languages.CompletionItemKind.Function,
    insertText: buildMacroCallSnippet(macro),
    insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
    documentation: buildMacroDocumentation(macro),
    range,
  }));
}

function getStatementKeywordInsertRange(linePrefix: string, position: EditorPosition): EditorRange {
  const tagStartIndex = linePrefix.lastIndexOf("{%");

  return {
    startLineNumber: position.lineNumber,
    endLineNumber: position.lineNumber,
    startColumn: tagStartIndex + 3,
    endColumn: position.column,
  };
}

function buildStatementKeywordSuggestions(
  monaco: MonacoInstance,
  position: EditorPosition,
  linePrefix: string,
  prefix: string
) {
  const range = getStatementKeywordInsertRange(linePrefix, position);

  const snippets: Array<{
    label: string;
    insertText: string;
    documentation: string;
    retriggerSuggest?: boolean;
  }> = [
    {
      label: "include",
      insertText: " include '${1}' ",
      documentation: "Include another template",
      retriggerSuggest: true,
    },
    {
      label: "import",
      insertText: " import '${1}' ",
      documentation: "Import a macro template as a namespace",
      retriggerSuggest: true,
    },
    /*{
      label: "from",
      insertText: " from '${1}' import ${2:macro_name} ",
      documentation: "Import specific macros into the current namespace",
      retriggerSuggest: true,
    },
    {
      label: "macro",
      insertText: " macro ${1:name}(${2:args}) %}\n${3}\n{% endmacro %}",
      documentation: "Define a local macro",
    },*/
    {
      label: "if",
      insertText: " if ${1:condition} ",
      documentation: "Start an if block",
    },
    {
      label: "for",
      insertText: " for ${1:item} in ${2:items} ",
      documentation: "Start a for block",
    },
    {
      label: "set",
      insertText: " set ${1:name} = ${2:value} ",
      documentation: "Assign a variable",
    },
    /*{
      label: "extends",
      insertText: " extends '${1}' ",
      documentation: "Extend a base template",
      retriggerSuggest: true,
    },*/
  ];

  return snippets
    .filter((item) => item.label.startsWith(prefix))
    .map((item) => ({
      label: item.label,
      kind: monaco.languages.CompletionItemKind.Keyword,
      insertText: item.insertText,
      insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
      documentation: item.documentation,
      range,
      command: item.retriggerSuggest ? triggerSuggestCommand : undefined,
    }));
}

function buildMacroCallSnippet(macro: MacroDefinition): string {
  if (!macro.args || macro.args.length === 0) {
    return `${macro.name}()`;
  }

  const placeholders = macro.args.map((arg, index) => `\${${index + 1}:${arg}}`).join(", ");

  return `${macro.name}(${placeholders})`;
}

function buildMacroDocumentation(macro: ResolvedMacroDefinition): string {
  if (macro.template) {
    return macro.description
      ? `${macro.description}\n\nImported from: ${macro.template}`
      : `Imported from: ${macro.template}`;
  }

  return macro.description ?? "";
}

function deriveTemplateAlias(templatePath: string): string {
  const fileName = templatePath.split("/").pop() ?? templatePath;

  return (fileName
    .replace(/\.(md\.)?jinja$|\.j2$/i, "")
    .replace(/[^a-zA-Z0-9_]+/g, "_")
    .replace(/^[^a-zA-Z_]+/, "") || "macros");
}

function getTemplateInsertRange(linePrefix: string, position: EditorPosition, quote: "'" | '"' | null): EditorRange {
  if (!quote) {
    return {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: position.column,
      endColumn: position.column,
    };
  }

  const quoteIndex = linePrefix.lastIndexOf(quote);

  return {
    startLineNumber: position.lineNumber,
    endLineNumber: position.lineNumber,
    startColumn: quoteIndex + 2,
    endColumn: position.column,
  };
}

function getCurrentIdentifierRange(
  position: EditorPosition,
  linePrefix: string
): { prefix: string; range: EditorRange } {
  const match = linePrefix.match(/([a-zA-Z_][\w]*)$/);
  const prefix = match?.[1] ?? "";

  return {
    prefix,
    range: {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: position.column - prefix.length,
      endColumn: position.column,
    },
  };
}

function collectImportedTemplateAliases(source: string): Record<string, string> {
  const aliases: Record<string, string> = {};
  const regex = /\{%\s*import\s+['"]([^'"]+)['"]\s+as\s+([a-zA-Z_][\w]*)(?:\s+(?:with|without)\s+context)?\s*%}/g;

  for (const match of source.matchAll(regex)) {
    aliases[match[2]] = match[1];
  }

  return aliases;
}

function collectDirectImportedMacros(source: string, catalog: PromptCatalog): ResolvedMacroDefinition[] {
  const result: ResolvedMacroDefinition[] = [];
  const regex = /\{%\s*from\s+['"]([^'"]+)['"]\s+import\s+(.+?)(?:\s+(?:with|without)\s+context)?\s*%}/g;

  for (const match of source.matchAll(regex)) {
    const templateName = match[1];
    const importList = splitTopLevelCsv(match[2]);
    const macroTemplate = catalog.macroTemplates.find((entry) => entry.template === templateName);

    if (!macroTemplate) {
      continue;
    }

    for (const importedEntry of importList) {
      const importMatch = importedEntry.match(/^([a-zA-Z_][\w]*)(?:\s+as\s+([a-zA-Z_][\w]*))?$/);

      if (!importMatch) {
        continue;
      }

      const originalName = importMatch[1];
      const aliasName = importMatch[2] ?? originalName;

      if (originalName.startsWith("_")) {
        continue;
      }

      const macro = macroTemplate.macros.find((entry) => entry.name === originalName);

      if (!macro) {
        continue;
      }

      result.push({
        ...macro,
        name: aliasName,
        template: templateName,
      });
    }
  }

  return result;
}

function collectLocalMacros(source: string): ResolvedMacroDefinition[] {
  const result: ResolvedMacroDefinition[] = [];
  // @ts-ignore TODO: Check for other solution
  const regex = /\{%\s*macro\s+([a-zA-Z_][\w]*)\s*\((.*?)\)\s*[-+]?%\}/gs;

  for (const match of source.matchAll(regex)) {
    const macroName = match[1];
    const rawArguments = match[2].trim();

    result.push({
      name: macroName,
      args: rawArguments ? splitTopLevelCsv(rawArguments) : [],
      description: "Local macro",
    });
  }

  return result;
}

function splitTopLevelCsv(input: string): string[] {
  const parts: string[] = [];
  let current = "";
  let quote: "'" | '"' | null = null;
  let escaped = false;
  let nestingLevel = 0;

  for (const char of input) {
    if (escaped) {
      current += char;
      escaped = false;
      continue;
    }

    if (quote) {
      current += char;

      if (char === "\\") {
        escaped = true;
        continue;
      }

      if (char === quote) {
        quote = null;
      }

      continue;
    }

    if (char === "'" || char === '"') {
      quote = char;
      current += char;
      continue;
    }

    if (char === "(" || char === "[" || char === "{") {
      nestingLevel += 1;
      current += char;
      continue;
    }

    if (char === ")" || char === "]" || char === "}") {
      nestingLevel = Math.max(0, nestingLevel - 1);
      current += char;
      continue;
    }

    if (char === "," && nestingLevel === 0) {
      const value = current.trim();
      if (value) {
        parts.push(value);
      }
      current = "";
      continue;
    }

    current += char;
  }

  const finalValue = current.trim();
  if (finalValue) {
    parts.push(finalValue);
  }

  return parts;
}
